import { Box } from "@mui/material";
import { tokens } from "../../theme";

import { formatTitle, normalizeText, sortNames, universityImage, } from "../../utils/utils";
import TargetMet from "../../components/targets/TargetMet";

import { useAuth } from "../../AuthContext";
import PageGuard from "../../components/PageGuard";
import LoadingState from "../../components/LoadingState";

import { useRedfidViews } from "../../utils/redfidView";

const normalizeMissing = (value, fallback = "Sin información") => {
  const raw = String(value ?? "").trim();

  if (
    !raw ||
    raw === "—" ||
    raw === "-" ||
    normalizeText(raw) === "sin informacion"
  ) {
    return fallback;
  }

  return raw;
};

const getFullName = (p) => {
  const fullName =
    p?.nombre_completo ??
    p?.nombreCompleto ??
    p?.name ??
    p?.["Indique su nombre y apellido"] ??
    `${p?.nombre ?? ""} ${p?.apellido ?? ""}`;

  return normalizeMissing(fullName);
};

const getProgramasFromParticipant = (p) => {
  const raw =
    p?.programa_categorias ??
    p?.programas ??
    p?.programa ??
    p?.programa_categorias_str ??
    p?.["Programas donde imparte"] ??
    [];

  const isValidProgram = (x) => {
    const value = normalizeMissing(x, "");
    return value && normalizeText(value) !== "sin informacion";
  };

  if (Array.isArray(raw)) {
    return raw.map((x) => normalizeMissing(x, "")).filter(isValidProgram);
  }

  return String(raw ?? "")
    .split(/[;,|]/)
    .map((x) => normalizeMissing(x, ""))
    .filter(isValidProgram);
};

const getDisciplinasFromParticipant = (p) => {
  const disciplinas = [];

  if (p?.is_formador_matematica) disciplinas.push("Matemática");
  if (p?.is_formador_lenguaje) disciplinas.push("Lenguaje");
  if (p?.is_formador_parvularia) disciplinas.push("Educación Parvularia");

  const raw =
    p?.disciplina ??
    p?.discipline ??
    p?.area ??
    p?.Área ??
    p?.["Disciplina"] ??
    "";

  if (raw) {
    String(raw)
      .split(/[;,|]/)
      .map((x) => normalizeMissing(x))
      .filter(Boolean)
      .forEach((x) => disciplinas.push(x));
  }

  return [...new Set(disciplinas.map(normalizeMissing))].filter(Boolean);
};

const countFrequency = (items = []) => {
  const counts = {};

  items.forEach((item) => {
    const label = normalizeMissing(item);
    counts[label] = (counts[label] ?? 0) + 1;
  });

  return Object.entries(counts)
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => {
      if (normalizeText(a.label) === "otros programas") return 1;
      if (normalizeText(b.label) === "otros programas") return -1;

      return String(a.label).localeCompare(String(b.label), "es", {
        sensitivity: "base",
      });
    });
};

const Universidades = () => {
  const auth = useAuth();
  const colors = tokens();
  const { loading, error, data } = useRedfidViews();

  if (loading) return <LoadingState message="" />;
  if (error) return <div>Error cargando datos: {String(error)}</div>;

  const universidades = data?.tables?.universidades ?? [];
  const participantesAll = data?.tables?.participantes ?? [];


  const canSeeList = !!auth?.user?.is_admin || !!auth?.user?.is_staff;

  return (
    <PageGuard
      title="UNIVERSIDADES"
      subtitle="Distribución de formadores por universidad"
    >
      <Box m="20px" ml="5px" pb="100px">
        <Box
          display="grid"
          gridTemplateColumns={{
            xs: "repeat(1, 1fr)",
            sm: "repeat(2, 1fr)",
            md: "repeat(3, 1fr)",
          }}
          gap="20px"
          mt={2}
        >
          {[...universidades]
            .sort((a, b) => (b.formadores_qtty ?? 0) - (a.formadores_qtty ?? 0))
            .map((u) => {
              const uniCode = String(u.id ?? "—").trim().toUpperCase();

              const rawUniName = normalizeMissing(
                u.universidad_full ?? u.nombre_universidad ?? u.id
              );

              const uniName =
                rawUniName.includes(" ") &&
                  (rawUniName === rawUniName.toUpperCase() ||
                    rawUniName === rawUniName.toLowerCase())
                  ? formatTitle(rawUniName)
                  : rawUniName;

              const imgSrc = universityImage(uniName);

              const participants = participantesAll
                .filter((row) =>
                  (Array.isArray(row?.universidad_keys) ? row.universidad_keys : [])
                    .map((key) => String(key ?? "").trim().toUpperCase())
                    .includes(uniCode)
                )
                .sort((a, b) =>
                  getFullName(a).localeCompare(getFullName(b), "es", {
                    sensitivity: "base",
                  })
                );

              const formadoresItems = sortNames(participants.map(getFullName));

              const programasItems = countFrequency(
                participants.flatMap(getProgramasFromParticipant)
              );

              const disciplinasItems = countFrequency(
                participants.flatMap(getDisciplinasFromParticipant)
              );

              return (
                <TargetMet
                  key={u.id}
                  variant="hero"
                  fullWidth
                  titleWrap
                  title={uniName}
                  value={u.formadores_qtty ?? participants.length}
                  valueLabel={{
                    singular: "formador/a",
                    plural: "formadores/as",
                  }}
                  imgSrc={imgSrc}
                  mediaSize={150}
                  imgFit="contain"
                  imgRound={false}
                  bgColor={colors.primary[200]}
                  sx={{ borderRadius: "18px" }}
                  columns={3}
                  detailMode="collapse"
                  expandedPaddingTop={12}
                  disableParticipantsDetail={!canSeeList}
                  participantsStatKey="formadores"
                  participantsLabelRegex={/formador|particip|persona/i}
                  stats={[
                    {
                      key: "formadores",
                      label: {
                        singular: "Formador/a",
                        plural: "Formadores/as",
                      },
                      value: formadoresItems.length,
                      items: formadoresItems,
                      showItemFrequency: false,
                    },
                    {
                      key: "programas",
                      label: {
                        singular: "Programa",
                        plural: "Programas",
                      },
                      value: programasItems.length,
                      items: programasItems,
                      showItemFrequency: true,
                    },
                    {
                      key: "disciplinas",
                      label: {
                        singular: "Disciplina",
                        plural: "Disciplinas",
                      },
                      value: disciplinasItems.length,
                      items: disciplinasItems,
                      showItemFrequency: true,
                    },
                  ]}
                />
              );
            })}
        </Box>
      </Box>
    </PageGuard>
  );
};

export default Universidades;